import {
  CarFinancePageText,
  FindACarText,
  HowItWorksText,
  UnderstandCarFinanceText,
  LenderPartnersText,
  Endpoints,
  ReadyToGetCarFinanceText,
  FinanceExplainedText,
  FindACarCtaText,
  CarFinanceHeadText,
} from '../types/locales'

const defaultEndpoints: Endpoints = {
  staticAssetBaseUrl: 'https://assets-production.cinch.co.uk',
}

const carFinanceHeadText: CarFinanceHeadText = {
  fallbackTitle:
    'UK Car Financing: Buy and Finance your car online with us - cinch',
  fallbackDescription:
    'cinch offers a range of car finance options helping you buy your next car without the faff. Find your new car and get it with a low deposit and simple monthly repayments.',
}

const understandCarFinance: UnderstandCarFinanceText = {
  title: 'Understanding car finance',
  ariaLabel: 'My list items',
  sections: [
    {
      title: 'How much car finance costs',
      content: `
      {boldP}The cost of your loan depends on:{/boldP}
      {list}how much the car costs<split>your deposit amount<split>the length of the term you choose - between 36 and 60 months<split>the interest rate you are offered.{/list}
      {info}APR (Annual Percentage Rate)<split>This means the amount of interest you’re charged each year, on top of what you’ve borrowed{/info}
      {p}You can also part-exchange your current car which will reduce the overall cost of your new car.{/p}
      {p}Make sure you can commit to the term of the loan, and that you can afford the monthly payments on top of your usual spending.{/p}
    `,
    },
    {
      title: 'What you’ll need to apply',
      content: `
      {boldP}To apply for car finance with cinch, you need to:{/boldP}
      {list}have a driving licence - full or provisional<split>apply with the same address that’s on your licence.{/list}
      {info}Car finance is subject to credit checks - it might be helpful to check your credit score before you apply{/info}
      {boldP}In the application, you’ll be asked for:{/boldP}
      {list}your name<split>date of birth and nationality<split>your recent address history<split>your employment status<split>your income and outgoings.{/list}
      {p}You’ll also be asked for your bank account details - this is so we can check that it’s really you applying.{/p}
      {p}When you submit, your application will go to our panel of lenders to see if they can offer you finance and could take up to 3 credit checks to find the right lender for you. This could temporarily reduce your ability to be approved for further credit.{/p}
    `,
    },
    {
      title: 'Confirming your offer',
      content: `
      {p}You’ll get an email to let you know if you’ve been approved or not. You can then view your finance offer.{/p}
      {p}Your tailored finance offer will show all the details of your loan.{/p}
      {p}If you’re happy with the offer, you’ll have 4 days to confirm it and sign.{/p}
      {h4}ID check and sign{/h4}
      {p}To confirm your offer, you’ll need to upload your ID for a check and sign digitally with our lender.{/p}
      {boldP}At this stage, you will need to:{/boldP}
      {list}upload a photo of your ID, like passport or driving licence<split>and upload a photo of your signature.<split>and then come back to cinch to book your delivery.{/list}
    `,
    },
    {
      title: 'HP and PCP: what’s the difference?',
      content: `
      {p}HP (Hire Purchase) and PCP (Personal Contract Purchase) are types of loans that help you pay for a car.{/p}
      {p}At cinch, you can choose to pay over 36 to 60 months.{/p}
      {boldP}With HP you:{/boldP}
      {list}pay monthly payments over a set time<split>own your car at the end of the plan.<split>you usually have to pay a small 'option to purchase' fee at the end of your HP plan. For Conditional Sale agreements, there is no such fee.{/list}
      {boldAndNormalText}If you want to end the agreement early<bold> - wear and tear charges may apply.{/boldAndNormalText}
      {hpLink}Learn more about HP car finance{/hpLink}
      {boldP}With PCP you:{/boldP}
      {list}pay monthly payments over a set time<split>keep, hand back or part exchange the car at the end - with an optional final payment if you choose to keep the car.{/list}
      {boldAndNormalText}If you want to hand back the car<bold> - this is subject to mileage and condition (excess mileage and damage charges may apply).{/boldAndNormalText}
      {boldAndNormalText}If you part exchange at the end<bold>  - this is subject to paying off your existing finance agreement and new credit agreements. New credit agreements are subject to status.{/boldAndNormalText}
      {pcpLink}Learn more about PCP car finance{/pcpLink}
      {info}It's important to give an accurate mileage if you apply for PCP. If you drive more miles, you'll have to pay an excess mileage charge{/info}
      {boldP}What's the difference between Hire Purchase (HP) and Conditional Sale (CS)?{/boldP}
      {p}When taking out a HP agreement, this may also include  a 'Condition Sale'.{/p}
      {p}Both HP and CS need monthly payments to cover the cost of the car, and with both contracts you'll be the legal owner of the car when you're all paid up.{/p}
      {p}HP  has a small 'option to purchase' fee included in your final payment, while CS has no such payment. You'll be aware of your 'option to purchase' fee at the start of your HP contract, so don't worry about being hit with large hidden costs.{/p}
      {p}CS only applies to HP finance, not PCP finance.{/p}
      {p}With any car finance agreement, it'll all be laid out in front of you right at the start.{/p}
    `,
    },
  ],
}

const howItWorks: HowItWorksText = {
  title: 'How it works',
  text: 'You can apply for finance once you’ve chosen your car and selected ‘Buy now’. Here’s how it works.',
  steps: [
    {
      title: 'Get a quote',
      splitText: [
        'Use our calculator to get a finance quote with ',
        ' plans to suit you. Deposit from as little as £0, use your part-exchange as deposit or top-up with extra cash if you need to.',
      ],
    },
    {
      title: 'Apply online',
      text: 'When you apply, we’ll ask about things like address history and affordability to get you a tailored finance offer.',
    },
    {
      title: 'Get a decision',
      text: 'Your application is sent off to our panel of lenders who will complete up to 3 credit searches. We’ll usually let you know within 24 hours if it has been approved.',
    },
    {
      title: 'Your offer',
      text: 'To confirm your offer, you’ll need to do a digital ID check and sign. You have 4 days to do this while your car is reserved.',
    },
  ],
}

const lenderPartners: LenderPartnersText = {
  title: 'Our lending partners',
  text: 'Our trusted partners work hard to find the right finance plan for you.',
  endpoints: defaultEndpoints,
}

const readyToGetCarFinance: ReadyToGetCarFinanceText = {
  title: 'Ready to get car finance?',
  text: "When you've chosen your new car, select 'Pay with finance' in checkout.",
  searchButtonText: 'Search all cars',
}

const findACar: FindACarText = {
  title: 'Find a car to suit your budget',
  alt: 'Image of a woman with a car key',
  searchSection: {
    subtitle: 'Search by finance type',
    PCP: {
      label: 'PCP (Personal Contract Purchase)',
      sublabel: 'Option to buy at the end, usually has lower monthly payments',
    },
    HP: {
      label: 'HP (Hire Purchase)',
      sublabel: 'Keep the car at the end, usually has higher monthly payments',
    },
  },
  inputSection: {
    subtitle: 'Enter your monthly budget',
    description:
      'Based on a £2,000 deposit on a 48 month plan with a representative APR 11.4%, £10 option-to-purchase fee and fixed interest rate of 11.4%. Subject to credit checks. Deposit amount for illustrative purposes only. The amount of deposit used towards your finance agreement may affect monthly payments.',
    hpBudgetDescription:
      'Search for a car {b}up to {calculateHpCarPrice}{/b} with a {budget} monthly budget on a HP plan.',
  },
  cta: {
    default: 'Search all cars',
    active: {
      message: 'Search for cars up to {budget}',
      suffix: {
        short: '/pm',
        long: ' a month',
        aria: ' per month',
      },
    },
  },
}

const findACarCta: FindACarCtaText = {
  ctaText: 'Find a car to suit your budget',
}

const financeExplained: FinanceExplainedText = {
  sectionTitle: 'Finance explained',
  videoTitle: 'Car Finance - ways to buy',
  cta: {
    start: 'Play',
    end: 'from Youtube',
  },
}

export const carFinancePageText: CarFinancePageText = {
  understandCarFinance,
  howItWorks,
  lenderPartners,
  readyToGetCarFinance,
  findACar,
  findACarCta,
  financeExplained,
  carFinanceHeadText,
}
